import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { feesService, type Invoice, type InvoiceStatus, type PaymentMethod, type DiscountType } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/lib/utils";
import { Loader2, Receipt, Tag, XCircle, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { FeeStructuresPanel } from "@/components/fees/FeeStructuresPanel";

const STATUS_VARIANT: Record<string, any> = {
  PENDING: "warning", PARTIAL: "info", PAID: "success", OVERDUE: "destructive", CANCELLED: "secondary",
  pending: "warning", paid: "success",
};

const METHODS: PaymentMethod[] = ["CASH", "UPI", "CARD", "BANK_TRANSFER", "CHEQUE", "OTHER"];

export function FeesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const isParent = user?.role === "parent";
  const [filter, setFilter] = useState<string>("OPEN");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [view, setView] = useState<"invoices" | "structures">("invoices");

  const { data: invoices = [], isPending } = useQuery({
    queryKey: ["fees", "list"],
    queryFn: () => feesService.list(),
  });

  const visible = invoices.filter((i) => {
    const s = (i.status || "").toUpperCase();
    if (filter === "OPEN") return ["PENDING", "PARTIAL", "OVERDUE"].includes(s);
    if (filter === "PAID") return s === "PAID";
    if (filter === "CANCELLED") return s === "CANCELLED";
    return true;
  });

  const totalOutstanding = invoices
    .filter((i) => ["PENDING", "PARTIAL", "OVERDUE"].includes((i.status || "").toUpperCase()))
    .reduce((acc, i) => acc + (i.balance_amount ?? i.amount), 0);
  const totalCollected = invoices.reduce((acc, i) => acc + (i.paid_amount ?? 0), 0);

  const subtitle = isAdmin
    ? `${invoices.length} invoices · ${formatINR(totalOutstanding)} outstanding · ${formatINR(totalCollected)} collected`
    : isParent
      ? `${invoices.length} invoice(s) for your child · ${formatINR(totalOutstanding)} due`
      : `${invoices.length} invoice(s) · ${formatINR(totalOutstanding)} due`;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fees</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setReportOpen(true)}><BarChart3 className="mr-2 h-4 w-4" /> Collection report</Button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex gap-2 border-b pb-2">
          <Button size="sm" variant={view === "invoices" ? "default" : "ghost"} onClick={() => setView("invoices")} data-testid="fees-view-invoices-btn">Invoices</Button>
          <Button size="sm" variant={view === "structures" ? "default" : "ghost"} onClick={() => setView("structures")} data-testid="fees-view-structures-btn">Fee Structures</Button>
        </div>
      )}

      {isAdmin && view === "structures" ? <FeeStructuresPanel /> : <>
      <div className="flex gap-2">
        {[["OPEN", "Open", "warning"], ["PAID", "Paid", "success"], ["CANCELLED", "Cancelled", "secondary"], ["ALL", "All", "outline"]].map(([k, l, v]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k as string)}>
            {l} <Badge variant={v as any} className="ml-2">
              {k === "ALL" ? invoices.length :
                k === "OPEN" ? invoices.filter((i) => ["PENDING", "PARTIAL", "OVERDUE"].includes((i.status || "").toUpperCase())).length :
                  k === "PAID" ? invoices.filter((i) => (i.status || "").toUpperCase() === "PAID").length :
                    invoices.filter((i) => (i.status || "").toUpperCase() === "CANCELLED").length}
            </Badge>
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : !visible.length ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">No invoices in this bucket.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((i) => {
                  const s = (i.status || "").toUpperCase();
                  const bal = i.balance_amount ?? i.amount;
                  return (
                    <TableRow key={i.id} className="cursor-pointer" onClick={() => setActiveId(i.id)}>
                      <TableCell className="font-semibold">{i.student_name}</TableCell>
                      <TableCell className="text-muted-foreground">{i.class_name}</TableCell>
                      <TableCell>{i.term}</TableCell>
                      <TableCell className="text-muted-foreground">{i.due_date}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatINR(i.amount)}</TableCell>
                      <TableCell className={`text-right tabular-nums font-bold ${bal > 0 ? "text-amber-700" : "text-emerald-700"}`}>{formatINR(bal)}</TableCell>
                      <TableCell><Badge variant={STATUS_VARIANT[s] || "secondary"}>{s}</Badge></TableCell>
                      <TableCell><Button size="sm" variant="ghost">Open</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </>}

      {activeId && <InvoiceDrawer id={activeId} onClose={() => setActiveId(null)} />}
      {reportOpen && <CollectionReportDialog onClose={() => setReportOpen(false)} />}
    </div>
  );
}

function InvoiceDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const canPay = isAdmin || user?.role === "parent";
  const qc = useQueryClient();
  const { data, isPending, refetch } = useQuery({ queryKey: ["fees", "detail", id], queryFn: () => feesService.detail(id) });
  const [payOpen, setPayOpen] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);

  const cancel = useMutation({
    mutationFn: () => feesService.cancel(id),
    onSuccess: () => { toast.info("Invoice cancelled"); qc.invalidateQueries({ queryKey: ["fees"] }); refetch(); },
    onError: (e: any) => toast.error(e.message),
  });
  const reverse = useMutation({
    mutationFn: (pid: string) => feesService.reversePayment(id, pid),
    onSuccess: () => { toast.info("Payment reversed"); qc.invalidateQueries({ queryKey: ["fees"] }); refetch(); },
    onError: (e: any) => toast.error(e.message),
  });

  const inv = data?.invoice;
  const status = (inv?.status || "").toUpperCase();

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Invoice
            {inv && <Badge variant={STATUS_VARIANT[status] || "secondary"}>{status}</Badge>}
          </DialogTitle>
          <DialogDescription>{inv ? `${inv.student_name} · ${inv.class_name} · ${inv.term}` : ""}</DialogDescription>
        </DialogHeader>
        {isPending || !data ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-md border bg-slate-50 p-4">
              <Stat label="Subtotal" value={formatINR(inv?.subtotal ?? inv?.amount ?? 0)} />
              <Stat label="Discount" value={`− ${formatINR(inv?.discount_total ?? 0)}`} tint="text-emerald-700" />
              <Stat label="Paid" value={formatINR(inv?.paid_amount ?? 0)} tint="text-sky-700" />
              <Stat label="Balance" value={formatINR(inv?.balance_amount ?? inv?.amount ?? 0)} tint={(inv?.balance_amount ?? 0) > 0 ? "text-amber-700" : "text-emerald-700"} bold />
            </div>

            {status !== "CANCELLED" && (
              <div className="flex flex-wrap gap-2">
                {(inv?.balance_amount ?? 0) > 0 && canPay && (
                  <Button onClick={() => setPayOpen(true)}><Receipt className="mr-2 h-4 w-4" /> {user?.role === "parent" ? "Pay fees" : "Record payment"}</Button>
                )}
                {isAdmin && (
                  <>
                    <Button variant="outline" onClick={() => setDiscOpen(true)}><Tag className="mr-2 h-4 w-4" /> Apply discount</Button>
                    <Button variant="outline" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                      <XCircle className="mr-2 h-4 w-4 text-destructive" /> Cancel invoice
                    </Button>
                  </>
                )}
              </div>
            )}

            <Section title="Discounts">
              {data.discounts.length === 0 ? <Empty>No discounts applied.</Empty> :
                <ul className="divide-y rounded-md border">
                  {data.discounts.map((d) => (
                    <li key={d.id} className="flex items-center justify-between p-3">
                      <div><div className="font-semibold">{d.type === "PERCENT" ? `${d.value}% off` : `${formatINR(d.value)} off`}</div>
                        <div className="text-xs text-muted-foreground">{d.reason || "—"} · by {d.applied_by_name}</div></div>
                    </li>
                  ))}
                </ul>}
            </Section>
            <Section title={`Payments (${data.payments.length})`}>
              {data.payments.length === 0 ? <Empty>No payments yet.</Empty> :
                <ul className="divide-y rounded-md border">
                  {data.payments.map((p) => (
                    <li key={p.id} className={`flex items-center justify-between p-3 ${p.reversed_at ? "opacity-60" : ""}`}>
                      <div>
                        <div className="font-semibold">{formatINR(p.amount)} · {p.method}{p.reversed_at && <span className="ml-2 text-xs text-destructive">REVERSED</span>}</div>
                        <div className="text-xs text-muted-foreground">{(p.received_at || "").slice(0, 10)} · {p.receipt_no}{p.reference ? ` · ${p.reference}` : ""}</div>
                      </div>
                      {!p.reversed_at && isAdmin && <Button size="sm" variant="ghost" onClick={() => reverse.mutate(p.id)}>Reverse</Button>}
                    </li>
                  ))}
                </ul>}
            </Section>
          </div>
        )}
        {payOpen && <PaymentDialog invoiceId={id} balance={inv?.balance_amount ?? 0} onClose={() => { setPayOpen(false); refetch(); }} />}
        {discOpen && <DiscountDialog invoiceId={id} onClose={() => { setDiscOpen(false); refetch(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ invoiceId, balance, onClose }: { invoiceId: string; balance: number; onClose: () => void }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => feesService.recordPayment(invoiceId, { amount: parseFloat(amount), method, reference: reference || undefined, note: note || undefined }),
    onSuccess: (r) => { toast.success(`Payment recorded — ${r.payment.receipt_no}`); qc.invalidateQueries({ queryKey: ["fees"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>Balance: {formatINR(balance)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(balance)} /></div>
          <div><Label>Method</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {METHODS.map((mm) => (
                <Button key={mm} type="button" size="sm" variant={method === mm ? "default" : "outline"} onClick={() => setMethod(mm)}>{mm}</Button>
              ))}
            </div></div>
          <div><Label>Reference (optional)</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} /></div>
          <div><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || !amount}>{m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiscountDialog({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const [type, setType] = useState<DiscountType>("FLAT");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => feesService.applyDiscount(invoiceId, { type, value: parseFloat(value), reason: reason || undefined }),
    onSuccess: () => { toast.success("Discount applied"); qc.invalidateQueries({ queryKey: ["fees"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Apply discount</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {(["FLAT", "PERCENT"] as DiscountType[]).map((t) => (
              <Button key={t} size="sm" variant={type === t ? "default" : "outline"} onClick={() => setType(t)}>{t}</Button>
            ))}
          </div>
          <div><Label>{type === "PERCENT" ? "Percent (0–100)" : "Amount (₹)"}</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} /></div>
          <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Sibling discount, scholarship…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || !value}>{m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollectionReportDialog({ onClose }: { onClose: () => void }) {
  const { data, isPending } = useQuery({ queryKey: ["fees", "report"], queryFn: () => feesService.collectionReport() });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Collection report</DialogTitle>
          <DialogDescription>All-time aggregates from /api/fees/reports/collection</DialogDescription>
        </DialogHeader>
        {isPending || !data ? <Loader2 className="h-6 w-6 animate-spin" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Collected" value={formatINR(data.collected_total)} tint="text-emerald-700" bold />
              <Stat label="Outstanding" value={formatINR(data.outstanding_total)} tint="text-amber-700" bold />
              <Stat label="Overdue" value={formatINR(data.overdue_total)} tint="text-red-700" bold />
            </div>
            <Section title="By method">
              <ul className="divide-y rounded-md border">
                {Object.entries(data.by_method).map(([m, v]) => (
                  <li key={m} className="flex justify-between p-2 text-sm"><span>{m}</span><span className="font-semibold">{formatINR(v)}</span></li>
                ))}
              </ul>
            </Section>
            <Section title="By class">
              <ul className="divide-y rounded-md border">
                {Object.entries(data.by_class).map(([m, v]) => (
                  <li key={m} className="flex justify-between p-2 text-sm"><span>{m}</span><span className="font-semibold">{formatINR(v)}</span></li>
                ))}
              </ul>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tint, bold }: { label: string; value: string; tint?: string; bold?: boolean }) {
  return (
    <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-1 ${bold ? "text-xl font-bold" : "text-base font-semibold"} ${tint || ""}`}>{value}</div></div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div><div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>{children}</div>;
}
function Empty({ children }: { children: React.ReactNode }) { return <div className="rounded-md border bg-slate-50 p-3 text-center text-xs text-muted-foreground">{children}</div>; }
